import React, { useState, useEffect } from 'react';
import { Layout, Menu, Input, Avatar, Dropdown, Space, Button, message } from 'antd';
import {
  DashboardOutlined,
  UserOutlined,
  NotificationOutlined,
  TeamOutlined,
  SettingOutlined,
  SearchOutlined,
  LogoutOutlined,
  RightOutlined,
  LeftOutlined,
  MenuOutlined,
  LockOutlined,
} from '@ant-design/icons';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import type { MenuProps } from 'antd';
import { supabase } from '../services/supabase';
import { menuService, MenuWithPermissions } from '../services/menuService';
import { logger } from '../utils/logger';
import { storageHelper } from '../utils/storageHelper';
import './MainLayout.css';

const { Header, Sider, Content } = Layout;

// 아이콘 맵핑
const iconMap: Record<string, React.ReactNode> = {
  DashboardOutlined: <DashboardOutlined />,
  UserOutlined: <UserOutlined />,
  NotificationOutlined: <NotificationOutlined />,
  TeamOutlined: <TeamOutlined />,
  SettingOutlined: <SettingOutlined />,
  MenuOutlined: <MenuOutlined />,
  LockOutlined: <LockOutlined />,
};

const MainLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => {
    // localStorage에서 사이드바 상태 불러오기
    const savedState = localStorage.getItem('sidebarCollapsed');
    return savedState === 'true';
  });
  const [userData, setUserData] = useState<any>(null);
  const [menuItems, setMenuItems] = useState<MenuProps['items']>([]);
  const [loadingMenus, setLoadingMenus] = useState(true);

  useEffect(() => {
    // 스토리지에서 사용자 정보 불러오기
    const storedUserData = storageHelper.getUserData();
    if (storedUserData) {
      setUserData(storedUserData);
      // 사용자 정보가 있으면 메뉴 로드
      loadUserMenus(storedUserData);
    }
  }, []);

  const loadUserMenus = async (user: any) => {
    setLoadingMenus(true);
    try {
      // 사용자 ID로 권한에 맞는 메뉴 조회
      const menus = await menuService.getUserMenus(user.id);
      
      // 메뉴 아이템으로 변환
      const items = convertToMenuItems(menus);
      setMenuItems(items);
    } catch (error) {
      logger.error('Error loading menus', error, { userId: user.id });
      // 에러 발생 시 기본 메뉴 사용
      setMenuItems(getDefaultMenuItems(user));
    } finally {
      setLoadingMenus(false);
    }
  };

  const convertToMenuItems = (menus: MenuWithPermissions[]): MenuProps['items'] => {
    return menus.map(menu => ({
      key: menu.path || menu.id,
      icon: menu.icon ? iconMap[menu.icon] || <MenuOutlined /> : <MenuOutlined />,
      label: menu.name,
      children: menu.children && menu.children.length > 0 
        ? convertToMenuItems(menu.children) 
        : undefined,
    }));
  };

  const getDefaultMenuItems = (user: any): MenuProps['items'] => {
    const defaultItems: MenuProps['items'] = [
      {
        key: '/dashboard',
        icon: <DashboardOutlined />,
        label: '대시보드',
      },
    ];

    // 관리자 권한에 따라 메뉴 추가
    if (user?.is_admin || user?.has_admin_role) {
      defaultItems.push(
        {
          key: '/users',
          icon: <UserOutlined />,
          label: '사용자 관리',
        },
        {
          key: '/notices',
          icon: <NotificationOutlined />,
          label: '공지사항',
        },
        {
          key: '/organizations',
          icon: <TeamOutlined />,
          label: '조직 관리',
        }
      );
    }

    // 슈퍼관리자만 접근 가능한 메뉴
    if (user?.is_admin) {
      defaultItems.push(
        {
          key: '/menus',
          icon: <MenuOutlined />,
          label: '메뉴 관리',
        },
        {
          key: '/menu-permissions',
          icon: <LockOutlined />,
          label: '메뉴 권한 관리',
        }
      );
    }

    defaultItems.push({
      key: '/settings',
      icon: <SettingOutlined />,
      label: '설정',
    });

    return defaultItems;
  };

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      label: '내 정보',
      icon: <UserOutlined />,
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: '로그아웃',
      icon: <LogoutOutlined />,
      danger: true,
    },
  ];

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    navigate(e.key);
  };

  const handleUserMenuClick: MenuProps['onClick'] = async (e) => {
    if (e.key === 'profile') {
      navigate('/profile');
    } else if (e.key === 'logout') {
      try {
        await supabase.auth.signOut();
        storageHelper.clearAll();
        message.success('로그아웃 되었습니다.');
        navigate('/login');
      } catch (error) {
        message.error('로그아웃 중 오류가 발생했습니다.');
      }
    }
  };

  return (
    <Layout className="main-layout">
      <Sider 
        trigger={null} 
        collapsible 
        collapsed={collapsed}
        className="layout-sider"
        width={240}
        style={{ position: 'relative' }}
      >
        <div className="logo" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }}>
          {collapsed ? '크린존' : '크린존 ADMIN'}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleMenuClick}
        />
        <Button
          className="sider-toggle-handle"
          type="primary"
          icon={collapsed ? <RightOutlined /> : <LeftOutlined />}
          onClick={() => {
            const newCollapsed = !collapsed;
            setCollapsed(newCollapsed);
            // localStorage에 상태 저장
            localStorage.setItem('sidebarCollapsed', String(newCollapsed));
          }}
          style={{
            position: 'absolute',
            right: collapsed ? '-20px' : '-20px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '20px',
            height: '80px',
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '0 8px 8px 0',
            fontSize: '12px',
            zIndex: 1000,
            boxShadow: '2px 0 8px rgba(0,0,0,0.15)'
          }}
        />
      </Sider>
      
      <Layout>
        <Header className="layout-header">
          <div className="header-left">
            <Input
              prefix={<SearchOutlined />}
              placeholder="검색..."
              className="header-search"
              style={{ width: 300 }}
            />
          </div>
          
          <div className="header-right">
            <Dropdown
              menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
              placement="bottomRight"
            >
              <Space className="header-user">
                <Avatar icon={<UserOutlined />} />
                <span>{userData?.name || '관리자'}</span>
              </Space>
            </Dropdown>
          </div>
        </Header>
        
        <Content className="layout-content">
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;